import type React from 'react';

import { Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

type Props = {
	name: string;
	value: string;
	checked: boolean;
	onChange: () => void;
	/** The icon tile or the thumbnail on the card's left. Decorative: the label names the choice. */
	leading: React.ReactNode;
	label: string;
	description?: string;
	/** Draws the radio circle on the card's right, as the group cards do. The destination cards have none. */
	showIndicator?: boolean;
	testId?: string;
};

/**
 * One choice card of the `variant_2` destination step and group list, after the prototype: a blue
 * border and a light blue fill when selected. A native radio sits under the card, so a group of cards
 * with one `name` gets the browser's arrow-key navigation and radio semantics. The DS `SelectionCard`
 * draws its selected state as a brand gradient ring, which the prototype does not use.
 */
export const RadioCard: React.FC<Props> = ({
	name,
	value,
	checked,
	onChange,
	leading,
	label,
	description,
	showIndicator = false,
	testId,
}) => (
	<label
		className={cn(
			'flex w-full cursor-pointer items-center gap-3 rounded-md border-2 p-3 transition-colors',
			'has-[:focus-visible]:outline-stroke-focus has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2',
			checked ? 'border-stroke-selected bg-fill-selected-weaker' : 'border-stroke-weaker hover:bg-fill-hover',
		)}
		data-testid={testId}
	>
		<input type="radio" className="sr-only" name={name} value={value} checked={checked} onChange={onChange} />
		<span aria-hidden="true" className="shrink-0">
			{leading}
		</span>
		<span className="flex min-w-0 flex-1 flex-col">
			<Text as="span" size="medium" fontWeight="bold" className="text-typography-strong line-clamp-2">
				{label}
			</Text>
			{description && (
				<Text as="span" size="small" className="text-typography-weak line-clamp-1">
					{description}
				</Text>
			)}
		</span>
		{showIndicator && (
			<span
				aria-hidden="true"
				className={cn(
					'flex size-6 shrink-0 items-center justify-center rounded-full border-2',
					checked ? 'border-stroke-selected' : 'border-stroke-weak',
				)}
			>
				{checked && <span className="bg-fill-selected-strong size-3 rounded-full" />}
			</span>
		)}
	</label>
);
